import { Team } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation, useTeamPermissions } from '../hooks';
import { deleteTeamFromCache } from '../queries/company-cache';

type UseDeleteTeamActionProps = {
    team: Team;
    /** Teams in the company. The last one cannot be deleted. */
    teamsCount: number;
};

type UseDeleteTeamActionData = {
    /** Whether this viewer can delete this team right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    deleteTeam: () => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Deleting a team: whether this viewer may do it, why not when they may not,
 * and the call itself. Kept together so a caller can wire up a button —
 * disabled state, tooltip, spinner and click — from one hook.
 */
export function useDeleteTeamAction(
    props: UseDeleteTeamActionProps
): UseDeleteTeamActionData {
    const { team, teamsCount } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const { canEditTeam } = useTeamPermissions({ teamId: team.id });

    const [isAllowed, reason] = useMemo(() => {
        if (!canEditTeam) {
            return [false, t('actions.delete_team.reason_not_permitted')];
        }

        // Whether the deletion is a valid move is still ours to work out.
        if (teamsCount <= 1) {
            return [false, t('actions.delete_team.reason_last_team')];
        }

        return [true, ''];
    }, [canEditTeam, teamsCount, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `DELETE /v3/teams/{id}` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'CANNOT_REMOVE_LAST_TEAM')) {
                return t('actions.delete_team.error_last_team');
            }
            if (isApiError(error, 'CANNOT_REMOVE_TEAM_WITH_OPEN_TASKS')) {
                return t('actions.delete_team.error_open_tasks');
            }
            // Both mean the same to the user: not a team they may delete.
            if (
                isApiError(error, 'CANNOT_REMOVE_TEAM') ||
                isApiError(error, 'TEAM_NOT_FOUND')
            ) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.teams.deleteTeam(team.id).then(() => {}),
        onSuccess: () => {
            deleteTeamFromCache(queryClient, team.id);

            UIToast.success(t('actions.delete_team.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        deleteTeam: () => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
